import { useState, useCallback } from 'react';
import {
  collectScreenQuestionIds,
  buildSkipRecordsForBypass,
  mergeSkipRecords,
  skippedQuestionIdSet,
  validateRequiredAtSubmit,
} from '../../../../utils/skipValidation';

/**
 * Custom hook for managing preview mode state and navigation
 * Extracts all preview-related logic from ScreenBuilder
 */
const usePreviewMode = ({ screens, navigationRules, loadNavigationRulesForQuestion, setSelectedElement }) => {
  // Preview mode state
  const [previewMode, setPreviewMode] = useState(false);
  const [previewScreenIndex, setPreviewScreenIndex] = useState(0);
  const [previewResponses, setPreviewResponses] = useState({});
  const [previewStartTime, setPreviewStartTime] = useState(null);
  const [previewScreenTimes, setPreviewScreenTimes] = useState({});
  const [previewScreenStartTime, setPreviewScreenStartTime] = useState(null);
  const [previewNavigationLog, setPreviewNavigationLog] = useState([]);
  const [previewCompleted, setPreviewCompleted] = useState(false);
  const [previewShowDebug, setPreviewShowDebug] = useState(false);
  const [previewValidationErrors, setPreviewValidationErrors] = useState({});
  const [previewSkipLogMessage, setPreviewSkipLogMessage] = useState(null);
  const [previewNavigationHistory, setPreviewNavigationHistory] = useState([]);
  // Track how many times a given screen has been visited in this session.
  // Once a screen is visited more than MAX_VISITS_PER_SCREEN, any skip rule
  // attempting to send the participant back to that screen is ignored and
  // the participant advances sequentially instead. This is a safety net
  // against circular skip-logic configurations; it doesn't prevent the
  // user from navigating wherever they want manually.
  const [previewScreenVisits, setPreviewScreenVisits] = useState({});
  // Rules that have already fired once in this preview session. A rule
  // fires at most once — otherwise the same cumulative/combination rule
  // would re-fire on every subsequent Next (its contributing answers
  // persist), trapping the participant and blocking chained skips.
  const [previewFiredRuleIds, setPreviewFiredRuleIds] = useState([]);
  // Questions the simulated participant has actually seen on screen.
  // Populated whenever a screen renders in preview (see markScreenVisited).
  // Used to distinguish "unanswered required" (blocker) from "never reached"
  // (fine) at submit time — mirrors SurveyTaking behaviour.
  const [previewVisitedQuestionIds, setPreviewVisitedQuestionIds] = useState(() => new Set());
  // Records of questions bypassed by skip-logic in preview mode — same
  // shape as the live session's `skippedQuestions`:
  //   [{question_id, skipped_by_rule_id}]
  // Fed into the submit-time required-validation check so bypassed required
  // questions don't falsely block completion.
  const [previewSkippedQuestions, setPreviewSkippedQuestions] = useState([]);

  /**
   * Pre-load navigation rules for all questions before entering preview mode
   * This ensures skip logic works for questions that weren't individually selected
   */
  const loadAllNavigationRulesForPreview = useCallback(async () => {
    const rulesToLoad = [];
    
    console.debug('[Preview Load] ========== CHECKING FOR RULES TO LOAD ==========');
    
    // Collect all questions with screen_question_id across all screens
    screens.forEach((screen, idx) => {
      console.debug(`[Preview Load] Screen ${idx + 1}:`, screen.name || screen.id);
      screen.elements?.forEach(element => {
        if (element.type === 'question') {
          console.debug(`[Preview Load]   Question element:`, {
            id: element.id,
            screen_question_id: element.screen_question_id,
            hasRulesAlready: !!navigationRules[element.id]
          });
          if (element.screen_question_id && !navigationRules[element.id]) {
            rulesToLoad.push({
              screenQuestionId: element.screen_question_id,
              elementId: element.id
            });
          } else if (!element.screen_question_id) {
            console.warn(`[Preview Load]   ⚠️ No screen_question_id for element ${element.id}`);
          }
        }
      });
    });
    
    console.debug(`[Preview Load] Rules to load: ${rulesToLoad.length}`, rulesToLoad);
    
    // Load rules in parallel
    if (rulesToLoad.length > 0) {
      console.debug(`[Preview] Loading navigation rules for ${rulesToLoad.length} questions...`);
      await Promise.all(
        rulesToLoad.map(({ screenQuestionId, elementId }) => 
          loadNavigationRulesForQuestion(screenQuestionId, elementId).catch(err => {
            console.error(`Failed to load rules for ${screenQuestionId}:`, err);
          })
        )
      );
      console.debug('[Preview Load] Rules after loading:', navigationRules);
    } else {
      console.debug('[Preview Load] No rules to load');
    }
    console.debug('[Preview Load] ========== END ==========');
  }, [screens, navigationRules, loadNavigationRulesForQuestion]);

  const recordScreenTime = useCallback((screenIndex) => {
    if (previewScreenStartTime) {
      const timeSpent = Date.now() - previewScreenStartTime;
      setPreviewScreenTimes(prev => ({
        ...prev,
        [screenIndex]: (prev[screenIndex] || 0) + timeSpent
      }));
    }
  }, [previewScreenStartTime]);

  // Mark every question on a screen as "visited" (seen by the participant).
  // Called whenever a screen becomes current, including on entry and back
  // navigation. Additive — never removes entries.
  const markScreenVisited = useCallback((screenIndex) => {
    const screen = screens[screenIndex];
    if (!screen) return;
    const ids = collectScreenQuestionIds(screen);
    if (ids.length === 0) return;
    setPreviewVisitedQuestionIds(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        const key = String(id);
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [screens]);

  const enterPreviewMode = useCallback(async (startIndex = 0) => {
    // Pre-load all navigation rules before entering preview
    await loadAllNavigationRulesForPreview();

    const now = Date.now();
    const validStartIndex = Math.max(0, Math.min(startIndex, screens.length - 1));
    setSelectedElement(null);
    setPreviewMode(true);
    setPreviewScreenIndex(validStartIndex);
    setPreviewResponses({});
    setPreviewStartTime(now);
    setPreviewScreenStartTime(now);
    setPreviewScreenTimes({});
    setPreviewNavigationLog([{
      timestamp: now,
      action: 'started',
      screen: screens[validStartIndex]?.name || `Screen ${validStartIndex + 1}`,
      screenIndex: validStartIndex
    }]);
    setPreviewNavigationHistory([]);
    setPreviewCompleted(false);
    setPreviewValidationErrors({});
    setPreviewSkipLogMessage(null);
    setPreviewScreenVisits({ [validStartIndex]: 1 });
    setPreviewFiredRuleIds([]);
    // Seed visited with the starting screen's questions; reset skipped set.
    const startIds = collectScreenQuestionIds(screens[validStartIndex]);
    setPreviewVisitedQuestionIds(new Set(startIds.map(String)));
    setPreviewSkippedQuestions([]);
  }, [screens, loadAllNavigationRulesForPreview, setSelectedElement]);

  const exitPreviewMode = useCallback(() => {
    setPreviewMode(false);
    setPreviewCompleted(false);
    setPreviewShowDebug(false);
    setPreviewSkipLogMessage(null);
  }, []);

  const navigatePreviewScreen = useCallback((nextIndex, skipReason = null, firingRuleId = null) => {
    // Record time on current screen
    recordScreenTime(previewScreenIndex);

    // Push current screen to navigation history (for back button)
    setPreviewNavigationHistory(prev => [...prev, previewScreenIndex]);

    // If this is a forward skip (target > currentIndex + 1), every
    // intervening screen's questions were bypassed — record them so the
    // submit-time required check treats them as legitimately skipped.
    if (firingRuleId != null || skipReason) {
      const bypass = buildSkipRecordsForBypass(
        previewScreenIndex, nextIndex, screens, firingRuleId
      );
      if (bypass.length > 0) {
        setPreviewSkippedQuestions(prev => mergeSkipRecords(prev, bypass));
      }
    }

    // Log navigation
    const logEntry = {
      timestamp: Date.now(),
      action: skipReason ? 'skip_logic' : 'navigation',
      from: screens[previewScreenIndex]?.name || `Screen ${previewScreenIndex + 1}`,
      to: screens[nextIndex]?.name || `Screen ${nextIndex + 1}`,
      fromIndex: previewScreenIndex,
      toIndex: nextIndex,
      skipReason,
      ruleId: firingRuleId || null,
      responses: { ...previewResponses }
    };

    setPreviewNavigationLog(prev => [...prev, logEntry]);

    // Show skip logic message if applicable
    if (skipReason) {
      setPreviewSkipLogMessage({
        message: skipReason,
        from: screens[previewScreenIndex]?.name || `Screen ${previewScreenIndex + 1}`,
        to: screens[nextIndex]?.name || `Screen ${nextIndex + 1}`
      });

      // Auto-hide after 4 seconds
      setTimeout(() => setPreviewSkipLogMessage(null), 4000);
    }

    // Navigate
    setPreviewScreenIndex(nextIndex);
    setPreviewScreenStartTime(Date.now());
    setPreviewValidationErrors({});
    setPreviewScreenVisits(prev => ({
      ...prev,
      [nextIndex]: (prev[nextIndex] || 0) + 1,
    }));
    // Mark the destination's questions as visited — we've actually shown them.
    markScreenVisited(nextIndex);
  }, [screens, previewScreenIndex, previewResponses, recordScreenTime, markScreenVisited]);

  const navigatePreviewBack = useCallback(() => {
    if (previewNavigationHistory.length === 0) {
      return;
    }

    // Record time on current screen
    recordScreenTime(previewScreenIndex);

    // Pop the last screen from history
    const previousScreenIndex = previewNavigationHistory[previewNavigationHistory.length - 1];
    setPreviewNavigationHistory(prev => prev.slice(0, -1));

    // Log navigation
    const logEntry = {
      timestamp: Date.now(),
      action: 'back',
      from: screens[previewScreenIndex]?.name || `Screen ${previewScreenIndex + 1}`,
      to: screens[previousScreenIndex]?.name || `Screen ${previousScreenIndex + 1}`,
      fromIndex: previewScreenIndex,
      toIndex: previousScreenIndex,
      responses: { ...previewResponses }
    };
    
    setPreviewNavigationLog(prev => [...prev, logEntry]);

    // Navigate
    setPreviewScreenIndex(previousScreenIndex);
    setPreviewScreenStartTime(Date.now());
    setPreviewValidationErrors({});
    // Back-navigation shows a screen the participant has seen before — still
    // mark visited (no-op if already there) so the invariant holds.
    markScreenVisited(previousScreenIndex);
  }, [screens, previewScreenIndex, previewResponses, previewNavigationHistory, recordScreenTime, markScreenVisited]);

  const completePreviewAssessment = useCallback(() => {
    recordScreenTime(previewScreenIndex);
    
    const completionTime = Date.now();
    const totalDuration = completionTime - previewStartTime;
    
    setPreviewNavigationLog(prev => [...prev, {
      timestamp: completionTime,
      action: 'completed',
      totalDuration,
      totalResponses: Object.keys(previewResponses).length
    }]);
    
    setPreviewCompleted(true);
  }, [previewScreenIndex, previewStartTime, previewResponses, recordScreenTime]);

  // validateCurrentScreen — used by PreviewMode on every Next click AND on
  // the final Finish click. Historically it ran per-screen required checks
  // and blocked forward navigation if a required question on the current
  // screen wasn't answered. That policy prevented skip-logic from legitimately
  // bypassing required questions.
  //
  // New policy:
  //   - On intra-survey Next (previewScreenIndex < lastIndex): allow freely.
  //     Skip-logic rules are authoritative; if a rule fires we honour it
  //     regardless of unanswered required questions on the current screen.
  //     If no rule fires, the participant is simply advancing sequentially —
  //     required questions will be caught at submit time if they stay
  //     unanswered through the full walk.
  //   - On Finish (previewScreenIndex === lastIndex): run the skip-aware
  //     submit-time required check. A required question only blocks if it
  //     was VISITED but not answered AND not bypassed by a skip rule.
  const validateCurrentScreen = useCallback((opts = {}) => {
    // A canvas Submit button can end the assessment from any screen, not just
    // the last array index — callers pass { isFinish: true } to force the
    // submit-time required check in that case.
    const isFinishClick = opts.isFinish === true || previewScreenIndex >= screens.length - 1;
    if (!isFinishClick) {
      // Intra-survey Next — let skip-logic rules decide.
      setPreviewValidationErrors({});
      return true;
    }

    // Mark the final screen's questions visited (the participant is seeing
    // them right now while pressing Finish), then validate across the whole
    // assessment with the visited-or-skipped rule.
    const lastIds = collectScreenQuestionIds(screens[previewScreenIndex]).map(String);
    const effectiveVisited = new Set([...previewVisitedQuestionIds, ...lastIds]);
    const skippedIds = skippedQuestionIdSet(previewSkippedQuestions);
    const missing = validateRequiredAtSubmit(
      screens,
      previewResponses,
      effectiveVisited,
      skippedIds,
    );
    if (missing.length === 0) {
      setPreviewValidationErrors({});
      return true;
    }
    const errors = {};
    for (const qid of missing) errors[qid] = 'This question is required';
    setPreviewValidationErrors(errors);
    return false;
  }, [screens, previewScreenIndex, previewResponses, previewVisitedQuestionIds, previewSkippedQuestions]);

  return {
    // State
    previewMode,
    previewScreenIndex,
    previewResponses,
    setPreviewResponses,
    previewStartTime,
    previewScreenTimes,
    previewNavigationLog,
    setPreviewNavigationLog,
    previewCompleted,
    previewShowDebug,
    setPreviewShowDebug,
    previewValidationErrors,
    previewSkipLogMessage,
    previewNavigationHistory,
    previewScreenVisits,
    previewFiredRuleIds,
    setPreviewFiredRuleIds,
    previewVisitedQuestionIds,
    previewSkippedQuestions,
    setPreviewSkippedQuestions,

    // Actions
    enterPreviewMode,
    exitPreviewMode,
    navigatePreviewScreen,
    navigatePreviewBack,
    completePreviewAssessment,
    validateCurrentScreen,
    loadAllNavigationRulesForPreview,
    markScreenVisited,
  };
};

export default usePreviewMode;
